import last from 'lodash/last';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  NavLink,
  useMatches,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLinkItem,
  DropdownMenuTrigger,
  Icon,
  VariantProps,
  cn,
  tv,
} from '@eluve/components';
import { FeatureFlagsEnum } from '@eluve/graphql-types';

import { useEnabledFeatureFlags } from './useIsFeatureFlagEnabled';

const tabNavLinkStyles = tv({
  base: 'border-b-2 border-transparent px-1 py-5 text-sm font-medium whitespace-nowrap hover:border-text-disabled',
  variants: {
    colorMode: {
      dark: 'text-text-tertiary hover:text-text-disabled',
      light: '',
    },
    isActive: {
      true: '',
    },
  },
  compoundVariants: [
    {
      colorMode: 'dark',
      isActive: true,
      className:
        'border-border-accent-secondary !text-text-accent-strong hover:border-border-accent-tertiary',
    },
    {
      colorMode: 'light',
      isActive: true,
      className:
        'border-border-accent-quaternary text-text-accent-secondary hover:border-bg-accent-strong-primary',
    },
  ],
});

const moreButtonStyles = tv({
  base: 'flex items-center gap-1 border-b-2 px-1 py-5 text-sm font-medium whitespace-nowrap',
  variants: {
    colorMode: {
      dark: 'border-transparent text-text-tertiary hover:border-text-disabled hover:text-text-disabled',
      light: 'border-transparent hover:border-text-disabled',
    },
    isActive: {
      true: '',
    },
  },
  compoundVariants: [
    {
      colorMode: 'dark',
      isActive: true,
      className: 'border-border-accent-secondary text-text-accent-strong',
    },
    {
      colorMode: 'light',
      isActive: true,
      className: 'border-border-accent-quaternary text-text-accent-secondary',
    },
  ],
});

type TabNavLinkProps = VariantProps<typeof tabNavLinkStyles>;

export type TabLink = {
  name: string;
  label?: React.ReactNode;
  link: string;
  featureFlag?: FeatureFlagsEnum;
  persistentQueryParams?: string[];
  /**
   * When true, the tab counts as active on any descendant route (matched by
   * path segment), not just the exact `link`. Use for tabs that own a detail
   * sub-route, e.g. `care-plans` staying active on `care-plans/:id`. The
   * desktop `NavLink` underline already matches descendants; this keeps the
   * mobile select and overflow menu in sync. Defaults to exact-match.
   */
  matchSubpaths?: boolean;
};

export type TabNavProps = {
  tabs: TabLink[];
  /**
   * Controls how overflow tabs are handled on larger screens:
   * - 'scroll' (default): horizontal scrolling for overflow tabs
   * - 'menu': overflow tabs are shown in a hamburger dropdown menu
   */
  overflowMode?: 'scroll' | 'menu';
  /** Accessible label for the navigation element */
  ariaLabel?: string;
  /**
   * Optional content rendered right-aligned in the tab bar row (desktop),
   * beside the tabs — for tab-scoped controls/actions. The active route can
   * portal its own controls in here so they sit next to the tabs instead of
   * above the tab's content.
   */
  actions?: React.ReactNode;
} & Pick<TabNavLinkProps, 'colorMode'>;

const buildTabLink = (
  link: string,
  persistentQueryParams: string[],
  currentQueryParams: URLSearchParams,
) => {
  if (!persistentQueryParams.length) {
    return link;
  }

  const newQueryParams = new URLSearchParams();
  persistentQueryParams.forEach((param) => {
    const value = currentQueryParams.get(param);
    if (value) {
      newQueryParams.set(param, currentQueryParams.get(param) ?? '');
    }
  });

  return `${link}?${newQueryParams.toString()}`;
};

type MoreButtonProps = {
  colorMode: TabNavLinkProps['colorMode'];
  isActive: boolean;
  tabs: TabLink[];
  params: URLSearchParams;
};

const MoreButton: React.FC<MoreButtonProps> = ({
  colorMode,
  isActive,
  tabs,
  params,
}) => {
  return (
    <div className="ml-8 shrink-0">
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="More navigation tabs"
          className={moreButtonStyles({
            colorMode,
            isActive,
          })}
        >
          <span>More</span>
          <Icon
            name="ChevronDown"
            className={colorMode === 'dark' ? 'text-text-tertiary' : undefined}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {tabs.map((tab) => (
            <DropdownMenuLinkItem
              key={tab.link}
              text={typeof tab.label === 'string' ? tab.label : tab.name}
              to={buildTabLink(
                tab.link,
                tab.persistentQueryParams ?? [],
                params,
              )}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const useOverflowTabs = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
  tabNames: string,
) => {
  const [hiddenTabNames, setHiddenTabNames] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) {
      setHiddenTabNames(new Set());
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const updateHiddenTabs = () => {
      const tabElements = container.querySelectorAll('[data-tab-name]');
      const containerRect = container.getBoundingClientRect();
      const hidden = new Set<string>();

      tabElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const tabName = (el as HTMLElement).dataset.tabName;
        if (tabName && rect.right > containerRect.right + 1) {
          hidden.add(tabName);
        }
      });

      setHiddenTabNames((prev) => {
        const prevArray = Array.from(prev).sort();
        const newArray = Array.from(hidden).sort();
        if (prevArray.join(',') === newArray.join(',')) {
          return prev;
        }
        return hidden;
      });
    };

    requestAnimationFrame(updateHiddenTabs);

    const resizeObserver = new ResizeObserver(updateHiddenTabs);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [containerRef, enabled, tabNames]);

  return hiddenTabNames;
};

export const TabNav: React.FC<TabNavProps> = ({
  tabs,
  colorMode = 'light',
  overflowMode = 'scroll',
  ariaLabel,
  actions,
}) => {
  const navigate = useNavigate();
  const matches = useMatches();
  const [params] = useSearchParams();
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  const enabledFeatureFlags = useEnabledFeatureFlags();

  const visibleTabs = useMemo(
    () =>
      tabs.filter((t) => {
        if (!t.featureFlag) {
          return true;
        }
        return enabledFeatureFlags.includes(t.featureFlag);
      }),
    [tabs, enabledFeatureFlags],
  );

  const lastMatch = last(matches);
  const matchedValue = visibleTabs.find((tab) => {
    if (tab.matchSubpaths) {
      return lastMatch?.pathname?.split('/').includes(tab.link) ?? false;
    }
    return (
      lastMatch?.pathname?.replaceAll('/', '')?.endsWith(tab.link) ?? false
    );
  });

  const tabNames = useMemo(
    () => visibleTabs.map((t) => t.name).join(','),
    [visibleTabs],
  );

  const hiddenTabNames = useOverflowTabs(
    tabsContainerRef,
    overflowMode === 'menu',
    tabNames,
  );

  const overflowTabs = useMemo(() => {
    if (overflowMode !== 'menu') return [];
    return visibleTabs.filter((tab) => hiddenTabNames.has(tab.name));
  }, [overflowMode, hiddenTabNames, visibleTabs]);

  const hasOverflowItem = overflowTabs.some(
    (tab) => tab.name === matchedValue?.name,
  );

  return (
    <div className="w-full">
      <div className="p-4 sm:hidden">
        <label htmlFor="tabs" className="sr-only">
          Select a tab
        </label>
        <select
          id="tabs"
          name="tabs"
          aria-label={ariaLabel ?? 'Select a tab'}
          className="block w-full rounded-md border-border-secondary py-2 pr-10 pl-3 text-md focus:border-border-accent-secondary focus:ring-border-accent-secondary focus:outline-hidden sm:text-sm"
          onChange={(e) => {
            const tab = tabs.find((tab) => tab.name === e.target.value);
            if (tab) {
              navigate(
                buildTabLink(tab.link, tab.persistentQueryParams ?? [], params),
              );
            }
          }}
          value={matchedValue?.name ?? tabs[0]?.name}
        >
          {visibleTabs.map((tab) => (
            <option key={tab.name}>{tab.name}</option>
          ))}
        </select>
      </div>

      <div
        className={cn(
          'hidden sm:block',
          overflowMode === 'scroll' && 'overflow-x-auto',
        )}
      >
        <div className="border-b border-border-primary pl-6">
          <nav
            className="-mb-px flex items-center"
            aria-label={ariaLabel ?? 'Tabs'}
          >
            <div
              ref={tabsContainerRef}
              className={cn(
                'flex min-w-0 flex-1 items-center space-x-8',
                overflowMode === 'menu' && 'overflow-hidden',
              )}
            >
              {visibleTabs.map((tab) => (
                <NavLink
                  key={tab.link}
                  data-tab-name={tab.name}
                  to={buildTabLink(
                    tab.link,
                    tab.persistentQueryParams ?? [],
                    params,
                  )}
                  className={({ isActive }) =>
                    tabNavLinkStyles({ isActive, colorMode })
                  }
                >
                  {tab.label ?? tab.name}
                </NavLink>
              ))}
            </div>
            {overflowMode === 'menu' && overflowTabs.length > 0 && (
              <MoreButton
                colorMode={colorMode}
                isActive={hasOverflowItem}
                tabs={overflowTabs}
                params={params}
              />
            )}
            {actions && (
              <div className="ml-8 flex shrink-0 items-center pr-6">
                {actions}
              </div>
            )}
          </nav>
        </div>
      </div>
    </div>
  );
};
